import { useCallback, useEffect, useMemo, useState } from 'react';
import logger from "../utils/logger";
import { filterBySearch } from '../utils/search';
const initialTaskFilters = {
  search: '',
  assigneeId: '',
  creatorId: '',
  status: '',
  tag: '',
  workspaceId: '',
  cabinetId: '',
  dealId: '',
  overdue: false,
  noDue: false,
  createdFrom: '',
  createdTo: '',
  dueFrom: '',
  dueTo: '',
  showDone: false
};
const taskStatusLabels = {
  created: 'Поставлена',
  in_progress: 'Выполняется',
  review: 'На проверке',
  rejected: 'Не принята',
  done: 'Выполнена'
};
export function useTasks({
  request,
  showStatus,
  setStatus,
  user,
  companyId,
  activeSection,
  activeProjectId
}) {
  const [tasks, setTasks] = useState([]);
  const [tasksBusy, setTasksBusy] = useState(false);
  const [tasksView, setTasksView] = useState('list');
  const [kanbanGroup, setKanbanGroup] = useState('status');
  const [kanbanTag, setKanbanTag] = useState('');
  const [taskFilters, setTaskFilters] = useState(initialTaskFilters);
  const loadTasks = useCallback(async () => {
    if (!companyId) return;
    setTasksBusy(true);
    setStatus?.(null);
    try {
      const workspaceFilter = taskFilters.workspaceId || (activeSection === 'projects' ? activeProjectId : '');
      const query = new URLSearchParams({
        company_id: companyId
      });
      if (workspaceFilter) {
        query.set('workspace_id', workspaceFilter);
      }
      if (taskFilters.dealId) {
        query.set('deal_id', taskFilters.dealId);
      }
      const data = await request(`/api/v1/tasks/?${query.toString()}`, {});
      setTasks(data);
    } catch (error) {
      logger.error(error);
      showStatus?.('error', 'Не удалось загрузить задачи.');
    } finally {
      setTasksBusy(false);
    }
  }, [activeProjectId, companyId, request, setStatus, showStatus, taskFilters.workspaceId, taskFilters.dealId]);
  useEffect(() => {
    if (activeSection === 'tasks' && companyId) {
      loadTasks().catch(error => {
        logger.error(error);
      });
    }
  }, [activeSection, companyId, loadTasks]);
  useEffect(() => {
    if (activeSection === 'tasks' && companyId) {
      loadTasks().catch(error => {
        logger.error(error);
      });
    }
  }, [activeSection, companyId, activeProjectId, taskFilters.workspaceId, loadTasks]);
  const taskTags = useMemo(() => {
    const tags = new Set();
    tasks.forEach(task => {
      (task.tags || []).forEach(tag => tags.add(tag));
    });
    return Array.from(tags);
  }, [tasks]);
  const baseFilteredTasks = useMemo(() => {
    let list = [...tasks];
    const searchValue = taskFilters.search.trim().toLowerCase();
    if (taskFilters.status) {
      list = list.filter(task => task.status === taskFilters.status);
    }
    if (taskFilters.assigneeId) {
      list = list.filter(task => task.assignee_id === taskFilters.assigneeId);
    }
    if (taskFilters.creatorId) {
      list = list.filter(task => task.created_by_id === taskFilters.creatorId);
    }
    if (taskFilters.workspaceId) {
      list = list.filter(task => task.workspace_id === taskFilters.workspaceId);
    }
    if (taskFilters.dealId) {
      list = list.filter(task => task.deal_id === taskFilters.dealId);
    }
    if (taskFilters.cabinetId) {
      list = list.filter(task => (task.cabinets || []).some(cabinet => cabinet.marketplace_account_id === taskFilters.cabinetId));
    }
    if (taskFilters.tag) {
      list = list.filter(task => (task.tags || []).includes(taskFilters.tag));
    }
    if (taskFilters.overdue) {
      list = list.filter(task => task.overdue);
    }
    if (taskFilters.noDue) {
      list = list.filter(task => !task.due_at);
    }
    if (taskFilters.createdFrom) {
      const from = new Date(taskFilters.createdFrom);
      list = list.filter(task => task.created_at && new Date(task.created_at) >= from);
    }
    if (taskFilters.createdTo) {
      const to = new Date(taskFilters.createdTo);
      list = list.filter(task => task.created_at && new Date(task.created_at) <= to);
    }
    if (taskFilters.dueFrom) {
      const from = new Date(taskFilters.dueFrom);
      list = list.filter(task => task.due_at && new Date(task.due_at) >= from);
    }
    if (taskFilters.dueTo) {
      const to = new Date(taskFilters.dueTo);
      list = list.filter(task => task.due_at && new Date(task.due_at) <= to);
    }
    if (searchValue) {
      list = filterBySearch(list, searchValue);
    }
    return list;
  }, [tasks, taskFilters]);
  const filteredTasks = useMemo(() => {
    let list = [...baseFilteredTasks];
    if (!taskFilters.showDone) {
      list = list.filter(task => task.status !== 'done');
    }
    return list;
  }, [baseFilteredTasks, taskFilters.showDone]);
  const sortedTasks = useMemo(() => {
    const list = [...filteredTasks];
    list.sort((a, b) => {
      const aDone = a.status === 'done';
      const bDone = b.status === 'done';
      if (aDone !== bDone) return aDone ? 1 : -1;
      const aDue = a.due_at ? new Date(a.due_at).getTime() : null;
      const bDue = b.due_at ? new Date(b.due_at).getTime() : null;
      if (aDue && bDue) return aDue - bDue;
      if (aDue && !bDue) return -1;
      if (!aDue && bDue) return 1;
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });
    return list;
  }, [filteredTasks]);
  const kanbanColumns = useMemo(() => {
    const kanbanTasks = baseFilteredTasks;
    const statuses = ['created', 'in_progress', 'review', 'rejected', 'done'];
    const visibleStatuses = taskFilters.showDone ? statuses : statuses.filter(status => status !== 'done');
    return visibleStatuses.map(status => ({
      key: status,
      title: taskStatusLabels[status],
      tasks: kanbanTasks.filter(task => task.status === status)
    }));
  }, [baseFilteredTasks, taskFilters.showDone]);
  const setPresetFilters = useCallback(preset => {
    const base = {
      ...initialTaskFilters
    };
    if (preset === 'creator') {
      base.creatorId = user?.id || '';
    }
    if (preset === 'assignee') {
      base.assigneeId = user?.id || '';
    }
    setTaskFilters(base);
  }, [user?.id]);
  return {
    tasks,
    tasksBusy,
    setTasksBusy,
    tasksView,
    setTasksView,
    kanbanGroup,
    setKanbanGroup,
    kanbanTag,
    setKanbanTag,
    taskFilters,
    setTaskFilters,
    taskTags,
    baseFilteredTasks,
    filteredTasks,
    sortedTasks,
    kanbanColumns,
    loadTasks,
    setPresetFilters,
    taskStatusLabels
  };
}
